import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { PortalShell, StatusBadge } from "@/components/business/B2BPortal";
import {
  fetchB2BGenerationRevealed,
  fetchB2BRecentGeneratedImages,
  fetchB2BGenerationStatus,
  fetchB2BStylePreviewURLs,
  getGenerationEnvironmentLabel,
  resolveB2BCandidateTargetCount,
  resolveEvaluatedCount,
  resolveB2BGeneratedImages,
  resolveGeneratedCount,
  resolveGenerationSessionId,
  resolvePassedCount,
  resolveRevealedPassedCount,
  startB2BGeneration,
  type B2BGeneratedImage,
  type B2BGenerationStatusResponse,
  type B2BQuestionnaireToggle,
} from "@/lib/b2bGenerationApi";
import { SEOHead } from "@/components/shared/SEOHead";
import { useBusinessPortalSnapshot } from "@/lib/b2bPortalApi";
import { type GenerationIntent } from "@/lib/b2bPortalData";

export const Route = createFileRoute("/business/portal/brands")({
  component: BrandAssetsPage,
});

const generationIntents: Array<{ value: GenerationIntent; label: string }> = [
  { value: "new_logo", label: "New logo" },
  { value: "logo_refresh", label: "Refresh existing logo" },
  { value: "packaging_artwork", label: "Packaging artwork" },
  { value: "repeat_pattern", label: "Repeat pattern" },
];

const packagingUses = ["Coffee bags", "Coffee cups", "Cup sleeves", "Gift bags", "Labels", "Tissue paper"];
const styleOptions = ["Premium", "Minimal", "Bold", "Heritage", "Organic", "Playful", "High contrast", "Warm"];
type QueueState = "idle" | "submitting" | "queued" | "generating" | "ready" | "failed";
type QuestionnaireStepId = "idea" | "text" | "style" | "constraints" | "review";

const questionnaireSteps: Array<{ id: QuestionnaireStepId; title: string; eyebrow: string }> = [
  { id: "idea", title: "Design idea", eyebrow: "Question 1" },
  { id: "text", title: "Text handling", eyebrow: "Question 2" },
  { id: "style", title: "Visual style", eyebrow: "Question 3" },
  { id: "constraints", title: "Use case + avoids", eyebrow: "Question 4" },
  { id: "review", title: "Review and generate", eyebrow: "Submit" },
];

const scriptStyleOptions = [
  { value: "clean_minimal", label: "Clean minimal", description: "Simple, modern marks that print cleanly on packaging." },
  { value: "bold_graphic", label: "Bold graphic", description: "High-contrast shapes with stronger shelf presence." },
  { value: "hand_drawn", label: "Hand drawn", description: "Organic linework, artisan labels, and approachable texture." },
  { value: "vintage", label: "Vintage", description: "Heritage packaging language with classic badge structure." },
  { value: "cute_playful", label: "Cute playful", description: "Friendly, expressive concepts for approachable brands." },
  { value: "retro_vector", label: "Retro vector", description: "Clean vector-style nostalgia with controlled color." },
  { value: "abstract", label: "Abstract", description: "Pattern-forward or symbolic marks for flexible systems." },
  { value: "dark_intense", label: "Dark intense", description: "Moody, premium contrast for stronger specialty brands." },
];

const scriptUseCases = [
  { value: "label", label: "Label / sticker" },
  { value: "mug", label: "Cup / drinkware" },
  { value: "tote_bag", label: "Bag / pouch" },
  { value: "poster", label: "Poster / insert" },
  { value: "general", label: "General brand asset" },
];

const defaultAvoidNotes = "avoid photorealistic product mockups, fake packaging folds, watermarks, and busy backgrounds; keep the design suitable for production handoff";

function BrandAssetsPage() {
  const { data: portalData, loading: portalLoading } = useBusinessPortalSnapshot();
  const tenantScopeKey = portalData.account?.id ?? "";
  const [intent, setIntent] = useState<GenerationIntent>("new_logo");
  const [packagingUse, setPackagingUse] = useState<string>(packagingUses[0] ?? "Coffee bags");
  const [prompt, setPrompt] = useState("");
  const [styles, setStyles] = useState<string[]>(["Premium", "High contrast"]);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [textOnlyMode, setTextOnlyMode] = useState<B2BQuestionnaireToggle>("no");
  const [exactText, setExactText] = useState("");
  const [styleChoice, setStyleChoice] = useState("clean_minimal");
  const [wantText, setWantText] = useState<B2BQuestionnaireToggle>("no");
  const [includeText, setIncludeText] = useState("");
  const [wantAvoidNotes, setWantAvoidNotes] = useState<B2BQuestionnaireToggle>("yes");
  const [avoidNotes, setAvoidNotes] = useState(defaultAvoidNotes);
  const [useCase, setUseCase] = useState("label");
  const [queueState, setQueueState] = useState<QueueState>("idle");
  const [sessionId, setSessionId] = useState("");
  const [status, setStatus] = useState<B2BGenerationStatusResponse | null>(null);
  const [queueError, setQueueError] = useState("");
  const [generatedImages, setGeneratedImages] = useState<B2BGeneratedImage[]>([]);
  const [recentImagesLoaded, setRecentImagesLoaded] = useState(false);
  const [selectedGeneratedImageId, setSelectedGeneratedImageId] = useState("");
  const [stylePreviewURLs, setStylePreviewURLs] = useState<Record<string, string>>({});
  const environmentLabel = getGenerationEnvironmentLabel();
  const candidateTargetCount = resolveB2BCandidateTargetCount();
  const activeStep = (questionnaireSteps[activeQuestionIndex] ?? questionnaireSteps[0])!;

  const conceptTitle = useMemo(() => {
    const clean = prompt.trim().replace(/\s+/g, " ");
    if (!clean) return "Describe the brand to prepare a generation brief";
    return clean.length > 78 ? `${clean.slice(0, 78)}...` : clean;
  }, [prompt]);

  const toggleStyle = (style: string) => {
    setStyles((current) => (
      current.includes(style)
        ? current.filter((item) => item !== style)
        : [...current, style]
    ));
  };

  const resetQueue = () => {
    if (queueState === "idle") return;
    setQueueState("idle");
    setSessionId("");
    setStatus(null);
    setQueueError("");
    setGeneratedImages([]);
  };

  useEffect(() => {
    let cancelled = false;
    fetchB2BStylePreviewURLs()
      .then((next) => {
        if (!cancelled) setStylePreviewURLs(next);
      })
      .catch(() => {
        if (!cancelled) setStylePreviewURLs({});
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setGeneratedImages([]);
    setSelectedGeneratedImageId("");
    setSessionId("");
    setStatus(null);
    setQueueState("idle");
    setQueueError("");
    setRecentImagesLoaded(false);

    if (!tenantScopeKey) {
      setRecentImagesLoaded(true);
      return () => {
        cancelled = true;
      };
    }

    fetchB2BRecentGeneratedImages()
      .then((response) => {
        if (cancelled) return;
        setGeneratedImages(resolveB2BGeneratedImages(response));
      })
      .catch((error) => {
        if (cancelled) return;
        setQueueError(error instanceof Error ? error.message : "Unable to load recent generated concepts");
      })
      .finally(() => {
        if (!cancelled) setRecentImagesLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantScopeKey]);

  useEffect(() => {
    if (generatedImages.length === 0) {
      setSelectedGeneratedImageId("");
      return;
    }
    if (!selectedGeneratedImageId || !generatedImages.some((image) => image.id === selectedGeneratedImageId)) {
      setSelectedGeneratedImageId(generatedImages[0]?.id ?? "");
    }
  }, [generatedImages, selectedGeneratedImageId]);

  useEffect(() => {
    if (!sessionId || queueState === "ready" || queueState === "failed") return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const next = await fetchB2BGenerationStatus(sessionId);
        if (cancelled) return;
        setStatus(next);
        const generated = resolveGeneratedCount(next);
        const evaluated = resolveEvaluatedCount(next);
        if (next.finished) {
          try {
            const revealed = await fetchB2BGenerationRevealed(sessionId);
            if (cancelled) return;
            const nextImages = resolveB2BGeneratedImages(revealed);
            setGeneratedImages(nextImages);
            setStatus((current) => current ? {
              ...current,
              passed_count: Math.max(resolvePassedCount(current), resolveRevealedPassedCount(revealed)),
              revealed_count: Math.max(current.revealed_count ?? current.revealedCount ?? 0, nextImages.length),
            } : next);
          } catch (error) {
            if (!cancelled) {
              setQueueError(error instanceof Error ? error.message : "Unable to load generated concepts");
            }
          }
          setQueueState("ready");
        } else if (generated > 0 || evaluated > 0) {
          setQueueState("generating");
        } else {
          setQueueState("queued");
        }
      } catch (error) {
        if (cancelled) return;
        setQueueError(error instanceof Error ? error.message : "Unable to read generation status");
        setQueueState("failed");
      }
    };

    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, 3500);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [queueState, sessionId]);

  const textStepComplete = textOnlyMode === "no" || exactText.trim().length > 0;
  const optionalTextComplete = wantText === "no" || includeText.trim().length > 0;
  const canAdvance = activeStep.id === "idea"
    ? prompt.trim().length > 0
    : activeStep.id === "text"
      ? textStepComplete && optionalTextComplete
      : true;
  const selectedStyle = scriptStyleOptions.find((item) => item.value === styleChoice) ?? scriptStyleOptions[0];
  const selectedUseCase = scriptUseCases.find((item) => item.value === useCase) ?? scriptUseCases[0];
  const activeGeneratedImage = useMemo(() => (
    generatedImages.find((image) => image.id === selectedGeneratedImageId) ?? generatedImages[0]
  ), [generatedImages, selectedGeneratedImageId]);
  const recentGeneratedImages = generatedImages.slice(1, 5);
  const liveBrandAssets = portalData.brandAssets;
  const hasArtworkForChecks = liveBrandAssets.length > 0 || generatedImages.length > 0;
  const hasUploadedArtwork = liveBrandAssets.length > 0;
  const hasVectorSource = liveBrandAssets.some((asset) => /\b(svg|ai|eps|pdf)\b/i.test(asset.fileType));
  const hasReadyAsset = liveBrandAssets.some((asset) => asset.quality === "ready");
  const intakeChecks = [
    {
      label: "Artwork file available",
      ready: hasArtworkForChecks,
      status: hasArtworkForChecks ? "Ready" : "Waiting",
    },
    {
      label: "Preview image available",
      ready: hasArtworkForChecks,
      status: hasArtworkForChecks ? "Ready" : "Waiting",
    },
    {
      label: "Vector/source file detected",
      ready: hasVectorSource,
      status: hasVectorSource ? "Ready" : hasUploadedArtwork ? "Review" : "Waiting",
    },
    {
      label: "Ready for proofing",
      ready: hasReadyAsset || generatedImages.length > 0,
      status: hasReadyAsset || generatedImages.length > 0 ? "Ready" : "Waiting",
    },
  ];
  const completedIntakeChecks = intakeChecks.filter((check) => check.ready).length;
  const intakeProgressPercent = Math.round((completedIntakeChecks / intakeChecks.length) * 100);

  const goToNextQuestion = () => {
    if (!canAdvance) return;
    setActiveQuestionIndex((current) => Math.min(current + 1, questionnaireSteps.length - 1));
  };

  const goToPreviousQuestion = () => {
    setActiveQuestionIndex((current) => Math.max(current - 1, 0));
  };

  const handleQueueGeneration = async () => {
    if (!prompt.trim() || !textStepComplete || !optionalTextComplete) return;
    setQueueState("submitting");
    setQueueError("");
    setStatus(null);
    setSessionId("");
    setGeneratedImages([]);
    try {
      const response = await startB2BGeneration({
        intent,
        packagingUse,
        prompt,
        styles,
        textOnlyMode,
        exactText,
        styleChoice,
        wantText,
        includeText,
        wantAvoidNotes,
        avoidNotes,
        useCase,
      });
      const nextSessionId = resolveGenerationSessionId(response);
      const startImages = resolveB2BGeneratedImages(response);
      if (startImages.length > 0) {
        setGeneratedImages(startImages);
        setSelectedGeneratedImageId(startImages[0]?.id ?? "");
      }
      setSessionId(nextSessionId);
      setQueueState(nextSessionId ? "queued" : "failed");
      if (!nextSessionId) {
        setQueueError("Generation queue accepted the request but did not return a session id.");
      }
    } catch (error) {
      setQueueError(error instanceof Error ? error.message : "Unable to submit generation request");
      setQueueState("failed");
    }
  };

  const generated = status ? resolveGeneratedCount(status) : 0;
  const evaluated = status ? resolveEvaluatedCount(status) : 0;
  const passed = Math.max(status ? resolvePassedCount(status) : 0, generatedImages.length);
  const visibleProgressCount = Math.max(generated, evaluated, passed, generatedImages.length);
  const progressPercent = queueState === "ready"
    ? 100
    : Math.max(0, Math.min(96, Math.round((visibleProgressCount / Math.max(candidateTargetCount, 1)) * 100)));
  const isWorking = queueState === "submitting" || queueState === "queued" || queueState === "generating";
  const queueLabel = queueState === "idle"
    ? "Not started"
    : queueState === "submitting"
      ? "Submitting"
      : queueState === "queued"
        ? "Queued"
        : queueState === "generating"
          ? "Generating"
          : queueState === "ready"
            ? "Ready"
          : "Needs setup";

  const renderQuestionStep = () => {
    if (activeStep.id === "idea") {
      return (
        <>
          <div className="b2b-question-card-header">
            <span className="b2b-kicker">{activeStep.eyebrow}</span>
            <h3>What should we make?</h3>
          </div>
          <div className="b2b-question-grid">
            <label className="b2b-field">
              <span>What do you need?</span>
              <select value={intent} onChange={(event) => { setIntent(event.target.value as GenerationIntent); resetQueue(); }}>
                {generationIntents.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </label>

            <label className="b2b-field">
              <span>Packaging use</span>
              <select value={packagingUse} onChange={(event) => { setPackagingUse(event.target.value); resetQueue(); }}>
                {packagingUses.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
            </label>
          </div>
          <label className="b2b-field b2b-field-wide">
            <span>Design idea</span>
            <textarea
              value={prompt}
              onChange={(event) => {
                setPrompt(event.target.value);
                resetQueue();
              }}
              placeholder="Example: premium coffee logo for a Kigali cafe."
              rows={6}
            />
          </label>
        </>
      );
    }

    if (activeStep.id === "text") {
      return (
        <>
          <div className="b2b-question-card-header">
            <span className="b2b-kicker">{activeStep.eyebrow}</span>
            <h3>Text on the artwork</h3>
          </div>
          <div className="b2b-question-group">
            <span className="b2b-field-label">Text-only mode</span>
            <div className="b2b-segmented-control" aria-label="Text-only mode">
              {(["no", "yes"] as B2BQuestionnaireToggle[]).map((value) => (
                <button key={value} type="button" className={textOnlyMode === value ? "is-selected" : ""} onClick={() => { setTextOnlyMode(value); resetQueue(); }}>
                  {value === "yes" ? "Exact text logo" : "Image or mark allowed"}
                </button>
              ))}
            </div>
          </div>
          {textOnlyMode === "yes" ? (
            <label className="b2b-field b2b-field-wide">
              <span>Exact text</span>
              <input value={exactText} onChange={(event) => { setExactText(event.target.value); resetQueue(); }} placeholder="Kigali Roast House" />
            </label>
          ) : (
            <div className="b2b-question-group">
              <span className="b2b-field-label">Include text in the design?</span>
              <div className="b2b-segmented-control" aria-label="Include text">
                {(["no", "yes"] as B2BQuestionnaireToggle[]).map((value) => (
                  <button key={value} type="button" className={wantText === value ? "is-selected" : ""} onClick={() => { setWantText(value); resetQueue(); }}>
                    {value === "yes" ? "Yes, include text" : "No text needed"}
                  </button>
                ))}
              </div>
            </div>
          )}
          {textOnlyMode === "no" && wantText === "yes" ? (
            <label className="b2b-field b2b-field-wide">
              <span>Text to include</span>
              <input value={includeText} onChange={(event) => { setIncludeText(event.target.value); resetQueue(); }} placeholder="Fresh roasted in Kigali" />
            </label>
          ) : null}
        </>
      );
    }

    if (activeStep.id === "style") {
      return (
        <>
          <div className="b2b-question-card-header">
            <span className="b2b-kicker">{activeStep.eyebrow}</span>
            <h3>Choose a style</h3>
          </div>
          <div className="b2b-script-style-grid">
            {scriptStyleOptions.map((option) => {
              const previewURL = stylePreviewURLs[option.value];
              const style = previewURL
                ? ({ "--b2b-style-preview": `url("${previewURL}")` } as CSSProperties)
                : undefined;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={[
                    "b2b-style-capsule",
                    styleChoice === option.value ? "is-selected" : "",
                    previewURL ? "has-preview" : "",
                  ].filter(Boolean).join(" ")}
                  style={style}
                  title={option.description}
                  onClick={() => {
                    setStyleChoice(option.value);
                    resetQueue();
                  }}
                >
                  <span className="b2b-style-capsule-preview" aria-hidden="true" />
                  <span className="b2b-style-capsule-label">{option.label}</span>
                </button>
              );
            })}
          </div>
          <div className="b2b-field-wide">
            <span className="b2b-field-label">Style notes</span>
            <div className="b2b-style-chip-grid" aria-label="Style direction">
              {styleOptions.map((style) => (
                <button
                  key={style}
                  type="button"
                  className={styles.includes(style) ? "is-selected" : ""}
                  onClick={() => { toggleStyle(style); resetQueue(); }}
                >
                  {style}
                </button>
              ))}
            </div>
          </div>
        </>
      );
    }

    if (activeStep.id === "constraints") {
      return (
        <>
          <div className="b2b-question-card-header">
            <span className="b2b-kicker">{activeStep.eyebrow}</span>
            <h3>Packaging details</h3>
          </div>
          <label className="b2b-field">
            <span>Use case</span>
            <select value={useCase} onChange={(event) => { setUseCase(event.target.value); resetQueue(); }}>
              {scriptUseCases.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <div className="b2b-question-group">
            <span className="b2b-field-label">Add avoid notes?</span>
            <div className="b2b-segmented-control" aria-label="Avoid notes">
              {(["yes", "no"] as B2BQuestionnaireToggle[]).map((value) => (
                <button key={value} type="button" className={wantAvoidNotes === value ? "is-selected" : ""} onClick={() => { setWantAvoidNotes(value); resetQueue(); }}>
                  {value === "yes" ? "Use safe print rules" : "No extra notes"}
                </button>
              ))}
            </div>
          </div>
          {wantAvoidNotes === "yes" ? (
            <label className="b2b-field b2b-field-wide">
              <span>Avoid notes</span>
              <textarea value={avoidNotes} onChange={(event) => { setAvoidNotes(event.target.value); resetQueue(); }} rows={4} />
            </label>
          ) : null}
        </>
      );
    }

    return (
      <>
        <div className="b2b-question-card-header">
          <span className="b2b-kicker">{activeStep.eyebrow}</span>
          <h3>Ready to generate</h3>
        </div>
        <div className="b2b-generation-preview b2b-field-wide">
          <div>
            <span className="b2b-kicker">Request</span>
            <strong>{conceptTitle}</strong>
            <p>{generationIntents.find((item) => item.value === intent)?.label} for {packagingUse} · {selectedStyle?.label} · {selectedUseCase?.label}</p>
          </div>
          <button className="b2b-primary-button" type="button" disabled={!prompt.trim() || isWorking} onClick={handleQueueGeneration}>
            {isWorking ? queueLabel : "Generate concepts"}
          </button>
        </div>
        <div className={`b2b-generation-queue b2b-field-wide is-${queueState}`}>
          <div>
            <span className="b2b-kicker">Image queue</span>
            <strong>{queueLabel}</strong>
            <p>
              {queueState === "idle"
                ? `Ready to submit to the ${environmentLabel} generation queue.`
                : queueState === "failed"
                  ? queueError || "The generation queue could not be reached."
                  : `Submitted to the ${environmentLabel} generation queue${sessionId ? ` · ${sessionId}` : ""}.`}
            </p>
          </div>
          <div className="b2b-generation-progress" aria-label="Generation progress">
            <span><b>{generated}</b> generated</span>
            <span><b>{evaluated}</b> evaluated</span>
            <span><b>{passed}</b> passed</span>
          </div>
          <div className="b2b-generation-meter" aria-label={`Generation ${progressPercent}% complete`}>
            <span style={{ width: `${progressPercent}%` }} />
          </div>
          {isWorking ? (
            <div className="b2b-generation-working" role="status" aria-live="polite">
              <span className="b2b-generation-spinner" aria-hidden="true" />
              <span>{queueState === "queued" ? "Waiting for the image worker" : "Generating and evaluating concepts"}</span>
            </div>
          ) : null}
            {generatedImages.length > 0 ? (
            <div className="b2b-generated-gallery b2b-generated-gallery-compact" aria-label="Generated concepts">
              {generatedImages.map((image, index) => (
                <article
                  key={image.id}
                  className={[
                    "b2b-generated-image-card",
                    image.id === selectedGeneratedImageId ? "is-selected" : "",
                  ].filter(Boolean).join(" ")}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setSelectedGeneratedImageId(image.id); setActiveQuestionIndex(0); }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedGeneratedImageId(image.id);
                      setActiveQuestionIndex(0);
                    }
                  }}
                >
                  <img src={image.imageUrl} alt={`Generated packaging concept ${index + 1}`} loading="lazy" />
                  <div>
                    <strong>Concept {index + 1}</strong>
                    {activeGeneratedImage?.id === image.id ? <span>Current concept</span> : <span>Ready for review</span>}
                  </div>
                </article>
              ))}
            </div>
          ) : isWorking ? (
            <div className="b2b-generated-gallery b2b-generated-gallery-skeleton" aria-hidden="true">
              {Array.from({ length: Math.min(3, candidateTargetCount) }).map((_, index) => (
                <div key={index} className="b2b-generated-image-skeleton" />
              ))}
            </div>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <PortalShell
      active="/business/portal/brands"
      eyebrow="Artwork"
      title="Logos & artwork"
      description="Create or upload artwork for your packaging orders."
      account={portalData.account}
    >
      <SEOHead title="Logos & Artwork" description="Create and manage artwork for Quuil packaging orders." path="/business/portal/brands" robots="noindex,follow" />

      <section className="b2b-card b2b-generation-panel">
        <div className="b2b-generation-copy">
          <span className="b2b-kicker">Create</span>
          <h2>Generate artwork</h2>
          <p>Answer a few questions, generate concepts, then use the best one in an order.</p>
          <ol className="b2b-generation-steps">
            <li>Describe the artwork</li>
            <li>Choose a style</li>
            <li>Generate concepts</li>
          </ol>
          <div className="b2b-action-row">
            <Link to="/business/portal/artwork" className="b2b-secondary-button">View artwork</Link>
            <Link to="/business/portal/projects/new" className="b2b-secondary-button">Start order</Link>
          </div>
        </div>

        <div className="b2b-generation-form b2b-questionnaire-form" aria-label="Artwork generation questionnaire">
          <nav className="b2b-questionnaire-rail" aria-label="Generation questionnaire steps">
            {questionnaireSteps.map((step, index) => (
              <button
                key={step.id}
                type="button"
                className={[
                  index === activeQuestionIndex ? "is-active" : "",
                  index < activeQuestionIndex ? "is-complete" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => setActiveQuestionIndex(index)}
              >
                <span>{index + 1}</span>
                <strong>{step.title}</strong>
              </button>
            ))}
          </nav>

          <div className="b2b-question-card">
            {renderQuestionStep()}
            <div className="b2b-questionnaire-actions">
              <button className="b2b-secondary-button" type="button" disabled={activeQuestionIndex === 0 || isWorking} onClick={goToPreviousQuestion}>
                Back
              </button>
              {activeStep.id !== "review" ? (
                <button className="b2b-primary-button" type="button" disabled={!canAdvance || isWorking} onClick={goToNextQuestion}>
                  Continue
                </button>
              ) : (
                <button className="b2b-secondary-button" type="button" disabled={isWorking} onClick={() => setActiveQuestionIndex(0)}>
                  Edit answers
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="b2b-section-heading">
        <div>
          <span className="b2b-kicker">Generated artwork</span>
          <h2>{activeGeneratedImage ? "Current concept" : "Concepts in progress"}</h2>
          {!recentImagesLoaded ? <p>Loading recent concepts...</p> : null}
        </div>
        {generatedImages.length > 0 ? (
          <Link to="/business/portal/artwork" className="b2b-secondary-button">
            View all artwork
          </Link>
        ) : null}
      </section>

      <section className="b2b-active-artwork-section">
        {activeGeneratedImage ? (
          <article className="b2b-card b2b-generation-card b2b-live-generation-card b2b-active-artwork-card">
            <img src={activeGeneratedImage.imageUrl} alt="Current generated brand concept" loading="lazy" />
              <div className="b2b-active-artwork-copy">
                <div className="b2b-card-row">
                <span className="b2b-kicker">Current concept</span>
                <StatusBadge status="ready" tone="good" />
              </div>
              <h3>Artwork draft</h3>
              <div className="b2b-generation-meta">
                <span>Ready</span>
              </div>
              <div className="b2b-active-artwork-actions">
                <Link to="/business/portal/projects/new" className="b2b-secondary-button">
                  Use in order
                </Link>
                <Link to="/business/portal/artwork" className="b2b-card-link">
                  View all
                </Link>
              </div>
            </div>
          </article>
        ) : (
          <article className="b2b-card b2b-empty-state b2b-active-artwork-empty">
            <h2>No current concept yet</h2>
            <p>Generate artwork above or upload an existing file to prepare an order.</p>
          </article>
        )}
      </section>

      {recentGeneratedImages.length > 0 ? (
        <section className="b2b-card b2b-recent-concepts-card">
          <div className="b2b-card-row">
            <div>
          <span className="b2b-kicker">Recent concepts</span>
          <h2>Other options</h2>
            </div>
            <Link to="/business/portal/artwork" className="b2b-card-link">Open gallery</Link>
          </div>
          <div className="b2b-recent-concepts-strip">
            {recentGeneratedImages.map((image, index) => (
              <button
                key={image.id}
                type="button"
                className={[
                  "b2b-recent-concept",
                  image.id === selectedGeneratedImageId ? "is-selected" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => {
                  setSelectedGeneratedImageId(image.id);
                  setActiveQuestionIndex(0);
                }}
              >
                <img src={image.imageUrl} alt={`Recent generated artwork ${index + 2}`} loading="lazy" />
                <span>Concept {index + 2}</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <section className="b2b-card b2b-upload-panel">
        <div>
          <span className="b2b-kicker">Upload</span>
          <h2>Upload logo or artwork</h2>
          <p>Add an existing logo, mark, PDF, SVG, or print file.</p>
        </div>
        <label className="b2b-primary-button b2b-file-button">
          Upload asset
          <input type="file" accept="image/*,.svg,.pdf,.ai,.eps" />
        </label>
      </section>

      <section className="b2b-card b2b-artwork-checks" aria-labelledby="artwork-checks-heading">
        <div className="b2b-artwork-checks-summary">
          <div>
            <span className="b2b-kicker">File readiness</span>
            <h2 id="artwork-checks-heading">
              {hasArtworkForChecks ? "Artwork intake checks" : "Artwork intake not started"}
            </h2>
            <p>
              {hasArtworkForChecks
                ? "Checks are based on artwork saved or generated in this customer workspace."
                : "Upload or generate artwork to start readiness checks for packaging proofs."}
            </p>
          </div>
          <strong>{completedIntakeChecks}/{intakeChecks.length}</strong>
        </div>
        <div className="b2b-artwork-checks-meter" aria-label={`${completedIntakeChecks} of ${intakeChecks.length} checks complete`}>
          <span style={{ width: `${intakeProgressPercent}%` }} />
        </div>
        <ol className="b2b-artwork-checks-list">
          {intakeChecks.map((check, index) => (
            <li key={check.label} className={check.ready ? "is-ready" : check.status === "Review" ? "is-review" : "is-waiting"}>
              <span aria-hidden="true">{index + 1}</span>
              <strong>{check.label}</strong>
              <small>{check.status}</small>
            </li>
          ))}
        </ol>
      </section>

      <section className="b2b-section-heading">
        <div>
          <span className="b2b-kicker">Saved files</span>
          <h2>Approved artwork</h2>
          <p>Artwork saved to this customer workspace.</p>
        </div>
      </section>

      {portalLoading ? (
        <section className="b2b-card b2b-loading-card">
          <h2>Loading saved artwork...</h2>
        </section>
      ) : liveBrandAssets.length > 0 ? (
        <section className="b2b-saved-artwork-list">
          {liveBrandAssets.map((asset) => (
          <article key={asset.id} className="b2b-card b2b-asset-card">
            <div className={`b2b-asset-preview bg-gradient-to-br ${asset.previewStyle}`}>
              <span>{asset.name.slice(0, 1)}</span>
            </div>
            <div className="b2b-card-row">
              <span className="b2b-kicker">{asset.type.replaceAll("_", " ")}</span>
              <StatusBadge status={asset.quality} tone={asset.quality === "ready" ? "good" : "warn"} />
            </div>
            <h3>{asset.name}</h3>
            <p>{asset.fileType} · {asset.dimensions}</p>
            <dl>
              <div>
                <dt>Original</dt>
                <dd>retained</dd>
              </div>
              <div>
                <dt>Preview PNG</dt>
                <dd>{asset.dimensions}</dd>
              </div>
              <div>
                <dt>Vector</dt>
                <dd>{asset.fileType.includes("SVG") ? "available" : "needed"}</dd>
              </div>
            </dl>
            {asset.warnings.length > 0 ? (
              <ul className="b2b-warning-list">
                {asset.warnings.map((warning) => <li key={warning}>{warning}</li>)}
              </ul>
            ) : (
              <p className="b2b-success-copy">Ready to use in an order.</p>
            )}
          </article>
          ))}
        </section>
      ) : (
        <section className="b2b-card b2b-empty-state">
          <h2>No saved artwork yet</h2>
          <p>Save a generated concept or upload a logo to make it available for orders.</p>
        </section>
      )}
    </PortalShell>
  );
}
